import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import BrandLogo from './BrandLogo';
import { motion } from 'framer-motion';

const { FiHome, FiCalendar, FiCheckSquare, FiSettings, FiPlus, FiBell, FiPlayCircle } = FiIcons;

const NAV_ITEMS = [
  { path: '/dashboard', icon: FiHome, label: 'Command Wall' },
  { path: '/calendar', icon: FiCalendar, label: 'Schedule' },
  { path: '/tasks', icon: FiCheckSquare, label: 'Checklists' },
  { path: '/demo', icon: FiPlayCircle, label: 'Demo' },
  { path: '/settings', icon: FiSettings, label: 'Settings' },
];

export default function Layout({ children, onQuickAdd }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden">
      {/* Desktop Sidebar (Denim Blue) */}
      <aside className="hidden md:flex flex-col w-72 bg-[#1a365d] text-white shadow-2xl z-20">
        <div className="p-8">
          <BrandLogo size="md" color="light" />
        </div>

        <nav className="flex-1 px-4 space-y-1.5">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all duration-300 group relative ${
                  isActive 
                    ? 'bg-white/10 text-yellow-300 shadow-inner' 
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {isActive && (
                  <motion.div 
                    layoutId="nav-pill"
                    className="absolute left-0 w-1 h-6 bg-yellow-300 rounded-r-full"
                  />
                )}
                <SafeIcon icon={item.icon} className={`text-xl transition-transform group-hover:scale-110 ${isActive ? 'text-yellow-300' : ''}`} />
                <span className="font-bold text-sm tracking-wide">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-8 space-y-4">
          <button 
            onClick={onQuickAdd}
            className="w-full flex items-center justify-center gap-2 bg-yellow-300 text-[#1a365d] py-4 rounded-2xl font-black hover:bg-yellow-400 transition-all shadow-xl shadow-yellow-400/10 active:scale-95 uppercase tracking-widest text-xs"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
            Quick Add
          </button>
          
          <div className="pt-6 border-t border-white/10 opacity-40 hover:opacity-100 transition-opacity">
             <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 text-center">
              Made by LingLink AI and DigLab AI
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full relative overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 bg-[#1a365d] text-white z-20 shadow-xl">
          <BrandLogo size="sm" color="light" />
          <div className="flex items-center gap-2">
            <button className="p-2 relative hover:bg-white/10 rounded-full transition-colors active:scale-90">
              <SafeIcon icon={FiBell} className="text-xl" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-yellow-300 rounded-full border-2 border-[#1a365d]"></span>
            </button>
          </div>
        </header>

        {/* Content Scroll Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-10 pb-32 md:pb-10 bg-[#f8fafc]">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-7xl mx-auto h-full"
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 flex justify-around p-3 pb-safe z-30 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] backdrop-blur-xl bg-white/90">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center p-2 rounded-2xl min-w-[64px] transition-all ${
                isActive ? 'text-[#1a365d] bg-[#1a365d]/5' : 'text-slate-400'
              }`}
            >
              <SafeIcon icon={item.icon} className={`text-2xl mb-1 ${isActive ? 'text-[#1a365d]' : ''}`} />
              <span className="text-[10px] font-black tracking-tighter uppercase">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}